/**
 * Whether the assistant's door is drawn, decided once and in one place.
 *
 * Three facts go in: what `GET /v1/agent/status` said (or that it has not
 * answered yet, or that it failed), and whether the screens are being drawn
 * through somebody else's eyes. Pure, so the rule can be proven without a
 * network or a store.
 *
 *   loading, unknown, failed   no door. A door that appears a second after the
 *                              header does is a jump; a door that is never
 *                              drawn on a server with no key is the design.
 *   unconfigured               no door. Decision 8 of the plan.
 *   View as                    no door. The band already says nothing tapped
 *                              writes, and the agent honours the same flag.
 *   no Ask OS                  no door. The person's role withholds OS
 *                              (`osAllows`), and the server refuses every
 *                              turn (OS_NOT_YOURS).
 */
import { LIVE_REFUSED_CLOSE, type AgentStatus } from '@alluzo/shared';

export type AgentStatusHeld =
  | { readonly kind: 'unknown' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'known'; readonly status: AgentStatus }
  | { readonly kind: 'failed' };

export const agentAvailable = (held: AgentStatusHeld, viewingAsSomeone: boolean, asksOs: boolean): boolean =>
  held.kind === 'known' && held.status.configured && !viewingAsSomeone && asksOs;

/**
 * The server's own sentence when it refused a live call outright: a close
 * with `LIVE_REFUSED_CLOSE` (Talk live switched off in the person's role,
 * `agent.talk_live_off`). Null for every other close, a 1008 among them
 * ('quota spent', 'send start first'), which the voice look names by its own
 * ending. A refusal is the role's answer, so the look offers no Try again.
 */
export const refusalOfClose = (code: number, reason: string): string | null =>
  code === LIVE_REFUSED_CLOSE && reason !== '' ? reason : null;

/**
 * Whether the composer's dictation streams its words live
 * (`/v1/agent/dictate/live`) or uploads the recording when it stops. A Google
 * key is exactly what the server's transcriber needs, so the status's own
 * `providers.google` answers it; an unknown status uploads.
 */
export const dictationLive = (held: AgentStatusHeld): boolean => held.kind === 'known' && held.status.providers.google;
